import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState, type ReactElement } from 'react'
import { Sun, Moon } from '@phosphor-icons/react'
import type { SchemaStationTimetable } from '../../schema/types'
import {
  dayKindFor,
  timetableForDayKind,
  type DayKind,
  type TimetableRow,
} from './timetableForDayKind'
import styles from './StationTimetableSection.module.css'

interface Props {
  /** Station display name for the heading, e.g. `Baker Street`. */
  stationName: string
  /** Line display name for the heading, e.g. `Bakerloo`. */
  lineDisplayName: string
  /** The PAGE LINE's record timetable (not the cluster aggregate). */
  timetable: SchemaStationTimetable | undefined
  /** Injectable clock — the picker defaults from the current date. */
  now?: Date
}

function TimeGroup({
  icon,
  label,
  rows,
  pick,
}: {
  icon: ReactElement
  label: string
  rows: readonly TimetableRow[]
  pick: (row: TimetableRow) => string | null
}): ReactElement | null {
  const { t } = useTranslation('station-page')
  if (rows.length === 0) return null
  return (
    <div className={styles.group}>
      <div className={styles.groupHeader}>
        {icon}
        <span className={styles.groupLabel}>{label}</span>
      </div>
      {rows.map((row) => {
        // Rows with no data stay VISIBLE with the schema's --:--
        // placeholder — mirroring StationDetails' TimetableRow on the
        // map, which renders the literal rather than hiding the
        // direction.
        const time = pick(row)
        return (
          <div key={row.towards} className={styles.row}>
            <span className={styles.towards}>
              {t('timetable_towards', { destination: row.towards })}
            </span>
            <span className={time === null ? styles.timePlaceholder : styles.time}>
              {time ?? '--:--'}
            </span>
          </div>
        )
      })}
    </div>
  )
}

function SkeletonGroup({ rowCount }: { rowCount: number }): ReactElement {
  return (
    <div className={styles.group} aria-hidden="true">
      <div className={styles.groupHeader}>
        <span className={styles.skeletonIcon} />
        <span className={styles.skeletonLabel} />
      </div>
      {Array.from({ length: Math.max(1, rowCount) }, (_, i) => (
        <div key={i} className={styles.row} data-testid="timetable-skeleton-row">
          <span className={styles.skeletonTowards} />
          <span className={styles.skeletonTime} />
        </div>
      ))}
    </div>
  )
}

/**
 * Timetable section — Figma 8597:41665 (TASK-699): first and last
 * trains of the page's line per direction, behind a Weekdays/Weekends
 * segmented picker that DEFAULTS from the current date (Sat/Sun →
 * Weekends). Weekend rows merge Saturday + Sunday (earliest first,
 * latest last — see timetableForDayKind).
 *
 * Data is the static schema timetable of the resolved line record —
 * transfers (?line-id=) re-key it. No timetable → the section renders
 * nothing (its nav stub copy stays honest).
 */
/** Shimmer length on a picker switch — long enough to read as a change. */
const SWITCH_SHIMMER_MS = 400

export function StationTimetableSection({
  stationName,
  lineDisplayName,
  timetable,
  now = new Date(),
}: Props): ReactElement | null {
  const { t } = useTranslation('station-page')
  const [kind, setKind] = useState<DayKind>(() => dayKindFor(now))
  // Bakerloo's weekday and weekend times barely differ — without a
  // visible transition the picker read as dead. A brief skeleton
  // shimmer acknowledges the switch before the new rows land.
  const [switching, setSwitching] = useState(false)
  const switchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (switchTimer.current !== null) clearTimeout(switchTimer.current)
    },
    [],
  )
  const pickKind = (next: DayKind): void => {
    if (next === kind) return
    setKind(next)
    setSwitching(true)
    if (switchTimer.current !== null) clearTimeout(switchTimer.current)
    switchTimer.current = setTimeout(() => {
      setSwitching(false)
    }, SWITCH_SHIMMER_MS)
  }
  const rows = timetableForDayKind(timetable, kind)

  if (timetable === undefined || timetable.directions.length === 0) return null

  return (
    <div className={styles.section}>
      <h2 className={styles.heading}>
        {t('timetable_heading', { line: lineDisplayName, station: stationName })}
      </h2>
      <div className={styles.picker} role="tablist" aria-label={t('timetable_tabs_aria')}>
        {(
          [
            { value: 'weekdays', label: t('timetable_tab_weekdays') },
            { value: 'weekends', label: t('timetable_tab_weekends') },
          ] as const
        ).map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={kind === tab.value}
            className={`${styles.pickerTab} ${kind === tab.value ? styles.pickerTabActive : ''}`}
            onClick={() => {
              pickKind(tab.value)
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className={styles.groups}>
        {switching ? (
          <>
            <SkeletonGroup rowCount={rows.length} />
            <SkeletonGroup rowCount={rows.length} />
          </>
        ) : (
          <>
            <TimeGroup
              icon={<Sun size={14} weight="fill" color="var(--sp-text-caption)" />}
              label={t('timetable_first_train')}
              rows={rows}
              pick={(row) => row.first}
            />
            <TimeGroup
              icon={<Moon size={14} weight="fill" color="var(--sp-text-caption)" />}
              label={t('timetable_last_train')}
              rows={rows}
              pick={(row) => row.last}
            />
          </>
        )}
      </div>
    </div>
  )
}
